import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {useLanguageSwitch} from './useLanguageSwitch';

/**
 * The props for the `LanguageSwitch` component.
 * Configures the language switcher that lets users toggle between available locales.
 */
interface ComponentProps {
    /** Optional `justify-content` value for the option list. */
    justify?: string;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'LanguageSwitch'
     */
    testId?: string;
}

/**
 * Renders a language switcher with a labeled section and pill-shaped buttons
 * for each available locale. The active locale is visually highlighted.
 * Switching triggers a Next.js locale route change which syncs with i18next.
 *
 * @param props         The component props.
 * @param props.justify Optional `justify-content` value for the option list.
 * @param props.testId  An optional test ID used for automation selectors. Defaults to `'LanguageSwitch'`.
 * @returns A JSX element rendering the LanguageSwitch.
 *
 * @example
 * ```tsx
 * <LanguageSwitch />
 * ```
 */
const LanguageSwitch = ({justify, testId = 'LanguageSwitch'}: ComponentProps) => {
    const {handleLanguageChange, options, t} = useLanguageSwitch();

    return (
        <Wrapper data-cy={testId}>
            <Label>{t('settings.language')}</Label>
            <OptionList $justify={justify}>
                {options.map(option => (
                    <Option
                        key={option.locale}
                        $isActive={option.isActive}
                        data-cy={`${testId}-${option.locale}`}
                        type="button"
                        onClick={() => handleLanguageChange(option.locale)}
                    >
                        {option.label}
                    </Option>
                ))}
            </OptionList>
        </Wrapper>
    );
};

LanguageSwitch.displayName = 'LanguageSwitch';

export {LanguageSwitch};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
`;

const Label = styled.span`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
`;

interface OptionStyleProps {
    /** Whether this option is currently selected. */
    $isActive: boolean;
}

interface OptionListStyleProps {
    $justify?: string;
}

const OptionList = styled.div<OptionListStyleProps>`
    display: flex;
    gap: ${spacing(3)};
    ${({$justify}) => $justify && `justify-content: ${$justify};`}
`;

const Option = styled.button<OptionStyleProps>`
    background: ${({$isActive, theme}) => ($isActive ? theme.colors.primaryDefault : theme.colors.surfaceHigh)};
    border: none;
    border-radius: ${spacing(6)};
    color: ${({$isActive, theme}) => ($isActive ? theme.colors.white : theme.colors.inkPrimary)};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 500;
    padding: ${spacing(3)} ${spacing(6)};
    transition: background 0.2s ease, color 0.2s ease;

    &:hover {
        background: ${({$isActive, theme}) => ($isActive ? theme.colors.primaryLight : theme.colors.surfaceMid)};
    }
`;